import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

const buttonSelector = 'cart-upsell-component button[data-action]';

async function handleCartUpsellClick(event) {
  const button = event.target instanceof Element ? event.target.closest(buttonSelector) : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const component = button.closest('cart-upsell-component');
  if (!(component instanceof HTMLElement)) return;

  const { action, variantId, lineKey } = button.dataset;
  if (!variantId || !action) return;

  const originalText = button.textContent;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Adding...';

  try {
    let response;

    if (action === 'upgrade') {
      if (!lineKey) {
        throw new Error('Cart line missing for bundle upgrade');
      }

      response = await replaceLineWithVariant(lineKey, variantId, component.dataset.sectionId);
    } else {
      response = await addVariant(variantId, component.dataset.sectionId);
    }

    const cart = response.item_count !== undefined ? response : await getCart();
    component.dispatchEvent(
      new CartUpdateEvent(cart, component.dataset.sectionId || '', {
        itemCount: cart.item_count,
        source: 'cart-upsell',
        variantId,
        sections: response.sections || {},
      })
    );
  } catch (error) {
    console.error('Cart upsell failed:', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = originalText;
  }
}

async function addVariant(variantId, sectionId) {
  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', '1');

  return requestCart(Theme.routes.cart_add_url, body, sectionId);
}

async function replaceLineWithVariant(lineKey, variantId, sectionId) {
  const sectionIds = getSectionIds(sectionId);
  const body = JSON.stringify({
    updates: {
      [lineKey]: 0,
      [variantId]: 1,
    },
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  });
  const config = fetchConfig('json', { body });
  const response = await fetch(Theme.routes.cart_update_url, {
    ...config,
    headers: {
      ...config.headers,
      Accept: 'application/json',
    },
  });
  const data = await response.json();

  if (!response.ok || data.errors || data.status >= 400) {
    throw new Error(data.description || data.message || 'Cart update failed');
  }

  return data;
}

async function requestCart(url, body, sectionId) {
  const sectionIds = getSectionIds(sectionId);
  if (sectionIds.length) {
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);
  }

  const config = fetchConfig('javascript', { body });
  const response = await fetch(url, {
    ...config,
    headers: {
      ...config.headers,
      Accept: 'application/json',
    },
  });
  const data = await response.json();

  if (!response.ok || data.errors || data.status >= 400) {
    throw new Error(data.description || data.message || 'Cart update failed');
  }

  return data;
}

async function getCart() {
  const cartUrl = Theme.routes.cart_url.endsWith('.js') ? Theme.routes.cart_url : `${Theme.routes.cart_url}.js`;
  const response = await fetch(cartUrl);

  if (!response.ok) {
    throw new Error('Could not refresh cart');
  }

  return response.json();
}

function getSectionIds(sectionId) {
  const sectionIds = new Set();

  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sectionIds.add(component.dataset.sectionId);
    }
  }

  if (sectionId) sectionIds.add(sectionId);

  return Array.from(sectionIds);
}

if (!window.__cartUpsellClickHandlerBound) {
  document.addEventListener('click', handleCartUpsellClick);
  window.__cartUpsellClickHandlerBound = true;
}
